import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Space,
  Tag,
  Divider,
  Avatar,
  Spin,
  Switch,
  Typography,
  Dropdown
} from 'antd';
import type { MenuProps } from 'antd';
import {
  LikeOutlined,
  StarOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import {
  TagIcon,
  StarIcon,
  TrashSimpleIcon,
  ChatTeardropDotsIcon,
  DotsThree
} from '@phosphor-icons/react';
import type { CloudPost } from '../types';
import { ImageCarousel } from './ImageCarousel';
import { CustomScrollbar } from './CustomScrollbar';
import { SquareCommentModal } from './SquareCommentModal';
import { formatCount } from '../utils/postList';
import { getSquareCommentCount } from '../apiClient';
import '../../styles/ImageCarousel.css';
import '../../styles/PostDetailModal.css';

const { Text } = Typography;

export interface PostDetailModalProps {
  /** 帖子数据 */
  post: CloudPost | null;
  /** 是否显示 */
  visible: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 是否加载中 */
  loading?: boolean;
  /** 运行环境 */
  platform: 'web' | 'electron';

  // ===== Electron 端专用 props =====
  /** 帖子更新回调 (electron) */
  onPostUpdated?: () => void;
  /** 打开外部链接回调 (electron) */
  onOpenExternal?: (url: string) => void;

  // ===== 图片相关 =====
  /** 图片列表（已处理好的 URL） */
  images?: string[];
  /** 是否有本地图片备份 (electron) */
  hasLocalBackup?: boolean;
  /** 图片备份加载中 (electron) */
  backupLoading?: boolean;
  /** 切换图片备份回调 (electron) */
  onToggleBackup?: (checked: boolean) => void;

  // ===== 视频相关 =====
  /** 视频 URL */
  videoUrl?: string;
  /** 视频文件大小 */
  videoSize?: string;
  /** 是否有本地视频备份 */
  hasVideoBackup?: boolean;
  /** 视频备份加载中 */
  videoBackupLoading?: boolean;
  /** 切换视频备份回调 */
  onToggleVideoBackup?: (checked: boolean) => void;

  // ===== 评论相关 =====
  /** 是否是云端帖子（云端帖子显示评论功能） */
  isCloudPost?: boolean;
  /** 初始评论数量 */
  initialCommentCount?: number;

  // ===== 云端帖子操作相关 =====
  /** 切换星标回调（云端帖子） */
  onCloudToggleStar?: () => void;
  /** 删除帖子回调（云端帖子） */
  onCloudDelete?: () => void;
  /** 编辑标签回调（云端帖子） */
  onCloudEditTags?: () => void;
  /** 云端帖子星标状态 */
  cloudIsStarred?: boolean;
}

/**
 * 共享的帖子详情弹窗组件
 * 同时支持 Web 端和 Electron 端
 */
export const PostDetailModal: React.FC<PostDetailModalProps> = ({
  post,
  visible,
  onClose,
  loading = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  platform: _platform, // 保留接口兼容性
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPostUpdated: _onPostUpdated, // 由 wrapper 组件处理
  onOpenExternal,
  // 图片
  images: propImages,
  hasLocalBackup = false,
  backupLoading = false,
  onToggleBackup,
  // 视频
  videoUrl,
  videoSize,
  hasVideoBackup = false,
  videoBackupLoading = false,
  onToggleVideoBackup,
  // 评论（仅云端帖子）
  isCloudPost = false,
  initialCommentCount = 0,
  // 云端帖子操作
  onCloudToggleStar,
  onCloudDelete,
  onCloudEditTags,
  cloudIsStarred = false,
}) => {
  const [images, setImages] = useState<string[]>([]);
  const [commentCount, setCommentCount] = useState(initialCommentCount);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Modal 关闭时暂停视频
  useEffect(() => {
    if (!visible && videoRef.current) {
      videoRef.current.pause();
    }
  }, [visible]);

  // 处理图片：优先使用 R2 备份图片
  useEffect(() => {
    if (propImages) {
      setImages(propImages);
      return;
    }

    if (!post) {
      setImages([]);
      return;
    }

    try {
      // 优先使用 R2 备份图片
      if (post.backupImages) {
        let backups: Array<string | { url: string }>;
        if (typeof post.backupImages === 'string') {
          backups = JSON.parse(post.backupImages);
        } else if (Array.isArray(post.backupImages)) {
          backups = post.backupImages;
        } else {
          backups = [];
        }

        if (Array.isArray(backups) && backups.length > 0) {
          const backupUrls = backups.map(item =>
            typeof item === 'string' ? item : item.url
          ).filter(Boolean);

          if (backupUrls.length > 0) {
            setImages(backupUrls);
            return;
          }
        }
      }

      // 没有备份图片时，使用原始网络图片（可能会被反爬）
      let networkImages: string[];
      if (Array.isArray(post.coverImages)) {
        networkImages = post.coverImages;
      } else if (typeof post.coverImages === 'string') {
        networkImages = JSON.parse(post.coverImages);
      } else {
        networkImages = [];
      }
      setImages(Array.isArray(networkImages) ? networkImages : []);
    } catch (e) {
      setImages([]);
    }
  }, [post, propImages]);

  // 弹窗打开时获取评论数量（仅云端帖子）
  useEffect(() => {
    if (visible && isCloudPost && post?.id) {
      getSquareCommentCount(post.id)
        .then((res) => setCommentCount(res.count))
        .catch((err) => console.error('[PostDetailModal] Failed to get comment count:', err));
    }
  }, [visible, isCloudPost, post?.id]);

  // 打开原帖链接
  const handleOpenOriginal = () => {
    if (!post?.postUrl) return;
    if (onOpenExternal) {
      onOpenExternal(post.postUrl);
    } else {
      window.open(post.postUrl, '_blank');
    }
  };

  // 打开博主主页
  const handleOpenBloggerProfile = () => {
    if (!post?.blogger?.profileUrl) return;
    if (onOpenExternal) {
      onOpenExternal(post.blogger.profileUrl);
    } else {
      window.open(post.blogger.profileUrl, '_blank');
    }
  };

  if (!post && !loading) return null;

  // 渲染操作按钮区域
  const renderActions = () => {
    if (!post) return null;

    // 更多菜单项
    const moreMenuItems: MenuProps['items'] = [];

    // 备份开关（仅本地帖子有回调时显示）
    if (onToggleBackup) {
      moreMenuItems.push({
        key: 'backup-image',
        label: (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 140 }}
          >
            <span>图片备份</span>
            <Switch
              size="small"
              checked={hasLocalBackup}
              onChange={onToggleBackup}
              loading={backupLoading}
            />
          </div>
        ),
      });

      if (post.isVideo && onToggleVideoBackup) {
        moreMenuItems.push({
          key: 'backup-video',
          label: (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 140 }}
            >
              <span>视频备份</span>
              <Switch
                size="small"
                checked={hasVideoBackup}
                onChange={onToggleVideoBackup}
                loading={videoBackupLoading}
              />
            </div>
          ),
        });
      }
    }

    // 删除选项
    if (onCloudDelete) {
      if (moreMenuItems.length > 0) {
        moreMenuItems.push({ type: 'divider' });
      }
      moreMenuItems.push({
        key: 'delete',
        label: '删除帖子',
        icon: <TrashSimpleIcon size={16} />,
        danger: true,
        onClick: onCloudDelete,
      });
    }

    return (
      <Space size={4} className="post-detail-actions">
        {/* 评论按钮 - 仅云端帖子显示 */}
        {isCloudPost && post?.id && (
          <SquareCommentModal
            postId={post.id}
            onCommentCountChange={setCommentCount}
            placement="bottomRight"
          >
            <Button
              type="text"
              className="comment-button"
              icon={<ChatTeardropDotsIcon size={18} weight="bold" />}
              title="查看评论"
            >
              {commentCount > 0 && (
                <span className="comment-count">{commentCount}</span>
              )}
            </Button>
          </SquareCommentModal>
        )}

        {/* 云端帖子操作按钮（Web/Electron 共用） */}
        {onCloudEditTags && (
          <Button
            type="text"
            icon={<TagIcon size={18} weight="bold" />}
            title="编辑标签"
            onClick={onCloudEditTags}
          />
        )}
        {onCloudToggleStar && (
          <Button
            type="text"
            icon={
              cloudIsStarred
                ? <StarIcon size={18} weight="fill" style={{ color: '#faad14' }} />
                : <StarIcon size={18} weight="bold" />
            }
            title={cloudIsStarred ? '取消星标' : '加星标'}
            onClick={onCloudToggleStar}
          />
        )}

        {/* 更多菜单（备份开关、删除） */}
        {moreMenuItems.length > 0 && (
          <Dropdown menu={{ items: moreMenuItems }} trigger={['click']} placement="bottomRight">
            <Button
              type="text"
              icon={<DotsThree size={18} weight="bold" />}
              title="更多"
            />
          </Dropdown>
        )}
      </Space>
    );
  };

  // 渲染左侧媒体区域
  const renderMediaSection = () => {
    // 获取实际的视频 URL：优先使用 prop，其次使用 R2 备份，最后使用原始 URL
    const actualVideoUrl = videoUrl || post?.backupVideoUrl || post?.videoUrl;

    // 视频播放器：Electron 有 videoUrl prop 或者 post 有 videoUrl 字段
    if (post?.isVideo && actualVideoUrl) {
      return (
        <div className="media-section video-section">
          <video
            ref={videoRef}
            controls
            className="video-player"
            poster={images.length > 0 ? images[0] : undefined}
          >
            <source src={actualVideoUrl} type="video/mp4" />
            您的浏览器不支持视频播放
          </video>
          {hasVideoBackup && videoSize && (
            <div className="video-size-badge">{videoSize}</div>
          )}
        </div>
      );
    }

    // 图片轮播
    return (
      <div className="media-section">
        <ImageCarousel
          images={images}
          showCounter={true}
          counterPosition="top-right"
        />
      </div>
    );
  };

  // 渲染右侧信息区域
  const renderInfoSection = () => {
    if (!post) return null;

    const infoContent = (
      <div className="info-content">
        {/* 博主信息 */}
        {post.blogger && (
          <div
            className="blogger-info"
            style={{ cursor: post.blogger.profileUrl ? 'pointer' : 'default' }}
            onClick={handleOpenBloggerProfile}
          >
            {post.blogger.avatarUrl && (
              <Avatar src={post.blogger.avatarUrl} size={40}>
                {post.blogger.nickname?.[0]}
              </Avatar>
            )}
            <div className="blogger-details">
              <span className="blogger-name">
                {post.blogger.nickname || '未知博主'}
              </span>
              {!!post.blogger.followersCount && (
                <Text type="secondary" className="blogger-followers">
                  粉丝 {formatCount(post.blogger.followersCount)}
                </Text>
              )}
            </div>
          </div>
        )}

        <Divider style={{ margin: '12px 0' }} />

        {/* 标签 */}
        <div className="post-tags">
          {post.tags && post.tags.length > 0 ? (
            post.tags.map((tag, index) => {
              const tagName = typeof tag === 'string' ? tag : (tag.name || '');
              const tagKey = typeof tag === 'string' ? tag : (tag.id || `tag-${index}`);
              return <Tag key={tagKey}>{tagName}</Tag>;
            })
          ) : (
            <Text type="secondary">暂无标签</Text>
          )}
        </div>

        {/* 标题 */}
        <h3
          className="post-title"
          onClick={handleOpenOriginal}
          style={{ cursor: post.postUrl ? 'pointer' : 'default' }}
          title={post.postUrl ? '点击跳转原帖' : undefined}
        >
          {post.title}
        </h3>

        {/* 发布日期 */}
        <div className="post-publish-date">
          <CalendarOutlined style={{ marginRight: 6 }} />
          <span>{post.publishDate || '未爬取发布时间'}</span>
        </div>

        {/* 正文 */}
        {post.content && (
          <div className="post-content">{post.content}</div>
        )}

        <Divider style={{ margin: '12px 0' }} />

        {/* 互动数据 */}
        <div className="post-stats">
          <Space size="large">
            <span>
              <LikeOutlined /> {formatCount(post.likedCount)}
            </span>
            {!!post.collectCount && (
              <span>
                <StarOutlined /> {formatCount(post.collectCount)}
              </span>
            )}
          </Space>
        </div>

        {/* 注：云端帖子评论通过 SquareCommentModal 处理，不在这里显示 */}
      </div>
    );

    return (
      <div className="info-section">
        {/* 顶部操作栏 */}
        <div className="info-header">
          {renderActions()}
        </div>

        {/* 内容区域使用自定义滚动条 */}
        <CustomScrollbar className="info-scroll-area">
          {infoContent}
        </CustomScrollbar>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="auto"
      centered
      className="shared-post-detail-modal"
      closeIcon={null}
      styles={{
        body: { padding: 0, height: '100%', display: 'flex' },
        wrapper: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        mask: { backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' },
        container: {
          background: 'transparent',
          boxShadow: 'none',
          height: 'calc(100vh - 48px)',
          maxHeight: 900,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <div className="modal-layout">
          {renderMediaSection()}
          {renderInfoSection()}
        </div>
      )}
    </Modal>
  );
};

export default PostDetailModal;
